import {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react"
import history from "utils/history"
import { useLocation } from "react-router-dom"

import { IEmployeesProvider, IEmployeesContext } from "./EmployeesProviderTypes"
import { EMPLOYEES_INITIAL_CONTEXT } from "./EmployeesProviderConstants"
import { UI_INITIAL_CONTEXT } from "providers/UIProvider/UIProviderConstants"

import { IEmployeesRes } from "api/querries/useGetEmployees"
import useGetEmployees from "api/querries/useGetEmployees"
import useAddEmployeeMutation from "api/mutations/employee/useAddEmployee"
import useUpdateEmployeeMutation from "api/mutations/employee/useUpdateEmployee"
import useDeleteEmployeeMutation from "api/mutations/employee/useDeleteEmployee"
import useGetEmployeeRoles from "api/querries/useGetEmployeeRoles"
import { IRoles, ISelectOption } from "types/shared"
import { useUI } from "providers/UIProvider"
import { useAppContext } from "providers/AppProvider/AppProvider"

export const EmployeesContext = createContext<IEmployeesContext>(
  EMPLOYEES_INITIAL_CONTEXT,
)

export const useEmployeesContext: () => IEmployeesContext = () =>
  useContext(EmployeesContext)

const EmployeesProvider: React.FC<IEmployeesProvider> = ({ children }) => {
  const { search } = useLocation()

  const { toggleModal, T, lang } = useUI()
  const { selectedBranches, user } = useAppContext()

  // @ts-ignore
  const hasAccess = user && user.roles && [IRoles.QAPP_ADMIN, IRoles.WASHER_ADMIN].includes(user?.roles[0]) || false;

  const params = new URLSearchParams(search)
  const pageSize = Number(params.get("pageSize"))

  const [employees, setEmployees] = useState<IEmployeesRes>({ data: [], itemCount: 0, skip: 0, take: 1 })
  const [currentPage, setCurrentPage] = useState(pageSize || 1)
  const [currentEmployeeId, setCurrentEmployeeId] = useState<number | null>(null)
  const [carWashIds, setCarWashIds] = useState<number[]>([])
  const [employeePositions, setEmployeePositions] = useState<ISelectOption[]>([])
  
  const { data: employeesData, isFetched, refetch: refetchEmployeesData } = 
    useGetEmployees( 
      { sortBy: "fullName", pageSize: 10, pageIndex: currentPage, carWashIds },
      hasAccess,
    )
    
  const { data: employeeRolesData } = useGetEmployeeRoles(hasAccess);
  const { mutate: addEmployee, isSuccess: addSuccess } = useAddEmployeeMutation()
  const { mutate: updateEmployee, isSuccess: editSuccess } = useUpdateEmployeeMutation()
  const { mutate: deleteEmployee, isSuccess: deleteSuccess } = useDeleteEmployeeMutation()


  const updateSearchParams = (params: any) => {
    history.push({
      search: `?${new URLSearchParams(params).toString()}`
    })
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    updateSearchParams({ pageSize: page })
  }

  const handleDeleteEmployee = (employeeId: number) => {
    if(employeeId) {
      deleteEmployee(employeeId)
    }
  }

  useEffect(()=>{
   hasAccess && refetchEmployeesData()
  },[currentPage])

  useEffect(() => {
    if (employeesData && isFetched) {
      setEmployees(employeesData)
    }
  }, [employeesData, isFetched])

  useEffect(()=>{
    if(employeeRolesData) {
      setEmployeePositions(employeeRolesData?.map(( employeeRole: any ) => ({
        label: `${T(`employees.positions.${employeeRole.name}`)}`,
        value: employeeRole.id
      }) ))
    }
  },[employeeRolesData, lang])

  useEffect(()=>{
    if(addSuccess || editSuccess) {
      toggleModal("addEditEmployeeModal", UI_INITIAL_CONTEXT.modals.addEditEmployeeModal.data)
      refetchEmployeesData()
    }

    if(deleteSuccess) {
      toggleModal("confirmationModal", false, UI_INITIAL_CONTEXT.modals.confirmationModal.data)
      refetchEmployeesData()
    }

  },[addSuccess, editSuccess, deleteSuccess])

  useEffect(()=>{
    if(selectedBranches !== undefined) {
      setCarWashIds(selectedBranches)
    }
  },[selectedBranches])

  const value = useMemo(
    () => ({
      employees,
      addEmployee,
      updateEmployee,
      employeePositions,
      handlePageChange,
      currentPage,
      handleDeleteEmployee,
      setCurrentEmployeeId
    }),
    [employees, employeePositions, currentEmployeeId],
  )

  return (
    <EmployeesContext.Provider value={value}>
      {children}
    </EmployeesContext.Provider>
  )
}

export default EmployeesProvider
